import React, { useMemo } from 'react'
import { FormattedMessage } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { SessionTypeText } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/messages'
import { isIntakeSession } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/utils'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { MEETING_FORMATS } from '@lyrahealth-inc/shared-app-logic/src/models/session/Session'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { ChatLiveIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/ChatLiveIcon'
import { LocationIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/LocationIcon'
import { PhoneIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/PhoneIcon'
import { VideoIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/VideoIcon'
import { LoadingSkeleton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/loadingSkeleton/LoadingSkeleton'
import { Tooltip } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/tooltip/Tooltip'

type ClientListSessionInfoProps = {
  appointment?: Appointment | null
  showModality?: boolean
  hideIcon?: boolean
}

const Container = styled.View(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing['8px'],
}))

const ClientListSessionInfo: React.FC<ClientListSessionInfoProps> = ({
  appointment,
  showModality = false,
  hideIcon = false,
}) => {
  const theme = useTheme()
  const meetingFormatIcon = useMemo(() => {
    switch (appointment?.meetingFormat) {
      case MEETING_FORMATS.IN_PERSON: {
        return <LocationIcon fillColor={theme.colors.iconDefault} size={24} />
      }
      case MEETING_FORMATS.PHONE:
      case MEETING_FORMATS.CALL: {
        return <PhoneIcon fillColor={theme.colors.iconDefault} size={24} />
      }
      case MEETING_FORMATS.LIVE_MESSAGING: {
        return <ChatLiveIcon fillColor={theme.colors.iconDefault} size={24} />
      }
      default: {
        return <VideoIcon fillColor={theme.colors.iconDefault} size={24} />
      }
    }
  }, [appointment?.meetingFormat, theme.colors.iconDefault])

  const text = useMemo(() => {
    if (showModality) {
      return appointment?.meetingFormat ? SessionTypeText[appointment.meetingFormat] : '-'
    }
    if (appointment && isIntakeSession(appointment)) {
      return <FormattedMessage defaultMessage='Intake' description='Intake session' />
    }
    return (
      <FormattedMessage
        defaultMessage='Follow up ({sessionNumber})'
        description='Follow up session with session number'
        values={{ sessionNumber: appointment?.sessionNumber }}
      />
    )
  }, [appointment, showModality])

  if (appointment === undefined) {
    return <LoadingSkeleton width={100} height={24} />
  }
  if (appointment === null) {
    return <BodyText text='-' color={theme.colors.textSecondary} />
  }
  return (
    <Container>
      {!hideIcon && appointment.meetingFormat && (
        <Tooltip content={SessionTypeText[appointment.meetingFormat]} hoverEnabled>
          {meetingFormatIcon}
        </Tooltip>
      )}
      <BodyText text={text} />
    </Container>
  )
}

export default ClientListSessionInfo
